import React, { memo, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Controller, useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormattedMessage, useIntl } from 'react-intl'
import NumberFormat from 'react-number-format'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid2'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import usePageProps from 'hooks/usePageProps'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import { defaultContactForm } from 'defaultForms'
import { useQuery } from 'react-query'
import axios from 'axios'
import { LEAD_TYPE } from 'enums'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import sx from './styles'

const DialogDynamic = dynamic(() => import('components/shared/Dialog'))

const Section2 = () => {
  const {
    shoppingPhone: companyPhone, whatsapp, whatsappWelcomeMessage, countryCode,
    contactPhoneDisplay,
  } = usePageProps()
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const { formatMessage: f } = useIntl()

  const {
    control, reset, handleSubmit, formState, getValues,
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .required(f({ id: 'CONTACT_FORM_LABEL_REQUIRED' })),
        zipCode: yup
          .string()
          .min(5, f({ id: 'CONTACT_PLEASE_ENTER_5_DIGITS' }))
          .max(5, f({ id: 'CONTACT_PLEASE_ENTER_5_DIGITS' }))
          .required(f({ id: 'CONTACT_FORM_LABEL_REQUIRED' })),
        email: yup
          .string()
          .email(f({ id: 'CONTACT_MUST_BE_VALID_EMAIL' }))
          .required(f({ id: 'CONTACT_FORM_LABEL_REQUIRED' })),
        phone: yup
          .string()
          .min(10, f({ id: 'CONTACT_PLEASE_ENTER_10_DIGITS' }))
          .max(10, f({ id: 'CONTACT_PLEASE_ENTER_10_DIGITS' }))
          .required(f({ id: 'CONTACT_FORM_LABEL_REQUIRED' })),
        message: yup.string(),
      }),
    ),
    defaultValues: defaultContactForm,
    mode: 'onChange',
  })

  const toggleAccepted = () => setPrivacyAccepted((prev) => !prev)

  const [name, zipCode, email, phone] = useWatch({
    control,
    name: ['name', 'zipCode', 'email', 'phone', 'message'],
  })

  const message = f({ id: 'CONTACT_SUCCESSFUL_SUBMIT_TEXT' }, {
    phone: `<a href="tel:${companyPhone}">${contactPhoneDisplay}</a>`,
    whatsapp: `<a target="_blank" rel="noopener noreferrer" href="//wa.me/${whatsapp}/?text=${whatsappWelcomeMessage}">Whatsapp</a>`,
  })

  const leadQuery = useQuery('lead', () => axios.post(`${process.env.NEXT_PUBLIC_API_ROOT}/api/leads`, {
    origin: window.location.origin,
    form_name: LEAD_TYPE.CONTACT,
    domain: window.location.host,
    content: JSON.stringify(getValues()),
  }), { enabled: formState.isSubmitted })

  const handleDialogClose = () => {
    reset(defaultContactForm)
    toggleAccepted()
  }

  const successMessage = (
    <Alert sx={sx.alert}>
      <AlertTitle>
        <FormattedMessage id="CONTACT_SUCCESSFUL_SUBMIT_TITLE" />
      </AlertTitle>
      <div dangerouslySetInnerHTML={{ __html: message }} />
    </Alert>
  )

  return (
    <Box sx={sx.root}>
      <Container maxWidth="md">
        <Grid
          component="form"
          onSubmit={handleSubmit(() => null)}
          container
          spacing={4}
          sx={sx.form}
        >
          <Grid size={12}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={f({ id: 'CONTACT_FORM_LABEL_FULLNAME' })}
                  value={field.value}
                  onChange={field.onChange}
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error && fieldState.error.message}
                  InputProps={{ className: fieldState.isTouched ? 'touched' : '' }}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
            }}
          >
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <NumberFormat
                  customInput={TextField}
                  label={f({ id: 'CONTACT_FORM_LABEL_PHONE' })}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.invalid}
                  helperText={fieldState.error && fieldState.error.message}
                  fullWidth
                  isNumericString
                  InputProps={{
                    className: fieldState.isTouched ? 'touched' : '',
                    startAdornment: <InputAdornment position="start">{`+${countryCode}`}</InputAdornment>,
                  }}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
            }}
          >
            <Controller
              name="zipCode"
              control={control}
              render={({ field, fieldState }) => (
                <NumberFormat
                  customInput={TextField}
                  label={f({ id: 'CONTACT_FORM_LABEL_ZIPCODE' })}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.invalid}
                  helperText={fieldState.error && fieldState.error.message}
                  fullWidth
                  isNumericString
                  InputProps={{ className: fieldState.isTouched ? 'touched' : '' }}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={sx.formInput}
                  label={f({ id: 'CONTACT_FORM_LABEL_EMAIL' })}
                  value={field.value}
                  onChange={field.onChange}
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error && fieldState.error.message}
                  InputProps={{ className: fieldState.isTouched ? 'touched' : '' }}
                  type="email"
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              control={control}
              name="message"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={sx.formInput}
                  label={f({ id: 'CONTACT_FORM_LABEL_MESSAGE' })}
                  value={field.value}
                  onChange={field.onChange}
                  fullWidth
                  multiline
                  rows={5}
                  error={fieldState.invalid}
                  helperText={fieldState.error && fieldState.error.message}
                  InputProps={{ className: fieldState.isTouched ? 'touched' : '' }}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              sx={sx.accept}
              label={(
                <>
                  <FormattedMessage id="ACCEPT_PRIVACY" />
                  {' '}
                  <Link
                    href={f({ id: 'ROUTE_PRIVACY' })}
                    passHref
                  >
                    <a
                      href={f({ id: 'ROUTE_PRIVACY' })}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FormattedMessage id="ACCEPT_PRIVACY_LINK" />
                    </a>
                  </Link>
                </>
              )}
              control={(
                <Checkbox
                  checked={privacyAccepted}
                  onChange={toggleAccepted}
                  name="privacy"
                  color="primary"
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Button
              disabled={!name || !zipCode || !email || !phone || leadQuery.status === 'loading'
                || !privacyAccepted}
              variant="contained"
              color="primary"
              type="submit"
              sx={sx.submitButton}
            >
              {formState.isSubmitting ? (
                <>
                  <CircularProgress
                    size={20}
                    sx={sx.circularProgressIcon}
                  />
                  <FormattedMessage id="FORM_SENDING" />
                </>
              ) : (
                <FormattedMessage id="FORM_SEND" />
              )}
            </Button>
          </Grid>
        </Grid>
      </Container>

      {
        leadQuery.data && formState.isSubmitted && (
          <DialogDynamic
            maxWidth="md"
            open
            onClose={handleDialogClose}
          >
            <Typography
              sx={sx.dialogTitle}
              align="center"
              variant="h3"
            >
              <FormattedMessage id="CONTACT_THANKS_FOR_CONTACT_US" />
            </Typography>
            {successMessage}
          </DialogDynamic>
        )
      }
    </Box>
  )
}

export default memo(Section2)
